import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Users, FileText, Inbox, Package, X, Loader2, Sparkles, CornerDownLeft, Mic, MicOff, FileSignature, Archive } from 'lucide-react';
import { API_URL } from '../utils/api';

const TYPE_META = {
  client:           { icon: Users,         color: 'text-blue-600',    bg: 'bg-blue-50',    label: 'Ügyfél',         href: (r) => `/clients` },
  invoice:          { icon: FileText,      color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Számla',         href: (r) => r.url || '/invoices' },
  incoming_invoice: { icon: Inbox,         color: 'text-purple-600',  bg: 'bg-purple-50',  label: 'Bejövő',         href: () => '/incoming-invoices' },
  contract:         { icon: FileSignature, color: 'text-violet-600',  bg: 'bg-violet-50',  label: 'Szerződés',      href: () => '/documents' },
  proposal:         { icon: FileText,      color: 'text-amber-600',   bg: 'bg-amber-50',   label: 'Ajánlat',        href: () => '/proposals' },
  service_type:     { icon: Package,       color: 'text-pink-600',    bg: 'bg-pink-50',    label: 'Szolgáltatás',   href: () => '/registry' },
  archive:          { icon: Archive,       color: 'text-slate-600',   bg: 'bg-slate-100',  label: 'Archívum',       href: () => '/document-archive' },
  // legacy fallbacks (old endpoint used different keys)
  incoming:         { icon: Inbox,         color: 'text-purple-600',  bg: 'bg-purple-50',  label: 'Bejövő',         href: () => '/incoming-invoices' },
  product:          { icon: Package,       color: 'text-amber-600',   bg: 'bg-amber-50',   label: 'Termék',         href: () => '/products' },
};

const EXAMPLE_COMMANDS = [
  '/új számla Kovács Kft részére 500000 Ft 14 napos határidővel',
  '/új ügyfél Horváth János horvath@example.com',
  '/új árajánlat Szabó Kft weboldal fejlesztés 1200000',
  '/menj a riportok oldalra',
  '/új termék Tanácsadás 50000 Ft 27%',
];

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const [aiParsing, setAiParsing] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const aiTimerRef = useRef(null);

  const voiceSupported = typeof window !== 'undefined'
    && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const isCommand = q.trim().startsWith('/');

  // Keyboard shortcut Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(v => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ(''); setResults([]); setSelected(0); setAiResult(null);
    }
  }, [open]);

  const doSearch = useCallback(async (query) => {
    if (!query || query.length < 1) { setResults([]); return; }
    setLoading(true);
    try {
      // New grouped endpoint: /api/search/global → flatten into typed rows
      const resp = await axios.get(`${API_URL}/api/search/global?q=${encodeURIComponent(query)}`, { withCredentials: true });
      const groups = resp.data?.groups || [];
      const flat = [];
      for (const g of groups) {
        for (const it of (g.items || [])) {
          flat.push({
            id: it.id,
            type: g.type,
            label: it.title,
            subtitle: it.subtitle,
            href: it.url,
          });
        }
      }
      setResults(flat);
      setSelected(0);
    } catch {
      setResults([]);
    } finally { setLoading(false); }
  }, []);

  const doAiCommand = useCallback(async (query) => {
    setAiParsing(true);
    setAiResult(null);
    try {
      const resp = await axios.post(`${API_URL}/api/search/ai-command`, { query }, { withCredentials: true });
      setAiResult(resp.data);
    } catch {
      setAiResult({ action: 'unknown', suggestion: 'AI parancs hiba' });
    } finally { setAiParsing(false); }
  }, []);

  useEffect(() => {
    // Clear both timers first
    if (timerRef.current) clearTimeout(timerRef.current);
    if (aiTimerRef.current) clearTimeout(aiTimerRef.current);

    if (isCommand) {
      setResults([]);
      const cleaned = q.trim().slice(1).trim();
      if (cleaned.length >= 3) {
        aiTimerRef.current = setTimeout(() => doAiCommand(q), 600);
      } else {
        setAiResult(null);
      }
    } else {
      setAiResult(null);
      timerRef.current = setTimeout(() => doSearch(q), 220);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (aiTimerRef.current) clearTimeout(aiTimerRef.current);
    };
  }, [q, isCommand, doSearch, doAiCommand]);

  const goResult = (r) => {
    const meta = TYPE_META[r.type];
    const target = r.href || (meta ? meta.href(r) : '/');
    navigate(target);
    setOpen(false);
  };

  const executeAiCommand = () => {
    if (!aiResult || !aiResult.href || aiResult.action === 'unknown') return;
    navigate(aiResult.href);
    setOpen(false);
  };

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
    }
    setListening(false);
  }, []);

  const startListening = useCallback(() => {
    if (!voiceSupported) {
      toast.error('A böngésződ nem támogatja a hangfelismerést. Próbáld Chrome-mal.');
      return;
    }
    if (listening) { stopListening(); return; }

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = 'hu-HU';
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 1;

    rec.onstart = () => setListening(true);
    rec.onerror = (e) => {
      setListening(false);
      if (e?.error === 'not-allowed') toast.error('Mikrofon hozzáférés megtagadva');
      else if (e?.error === 'no-speech') toast('Nem hallottam semmit', { duration: 2000 });
      else toast.error('Hangfelismerés hiba');
    };
    rec.onend = () => setListening(false);
    rec.onresult = (e) => {
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const tr = e.results[i][0].transcript;
        if (e.results[i].isFinal) final += tr;
        else interim += tr;
      }
      const text = (final || interim).trim();
      if (!text) return;
      // Always route voice to AI command mode (prefix with /)
      setQ(`/${text}`);
      if (final) {
        setListening(false);
      }
    };

    recognitionRef.current = rec;
    try { rec.start(); } catch { setListening(false); }
  }, [voiceSupported, listening, stopListening]);

  // Cleanup recognition on unmount / close
  useEffect(() => {
    if (!open && listening) stopListening();
    return () => stopListening();
  }, [open, listening, stopListening]);

  const onKeyDown = (e) => {
    if (isCommand) {
      if (e.key === 'Enter' && aiResult && aiResult.href) {
        e.preventDefault();
        executeAiCommand();
      }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(i => Math.min(i + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && results[selected]) { goResult(results[selected]); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="global-search-btn"
        className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-400 hover:bg-slate-50 transition-colors w-full max-w-xs min-w-0"
        style={{ backgroundColor: 'var(--background-surface)' }}
      >
        <Search size={14} className="flex-shrink-0" />
        <span className="flex-1 text-left truncate whitespace-nowrap"><span className="hidden sm:inline">Keresés vagy </span><kbd className="text-[10px] font-semibold" style={{ color: 'var(--accent-copper)' }}>/</kbd><span className="sm:hidden"> Keresés</span><span className="hidden sm:inline"> parancs…</span></span>
        <kbd className="hidden sm:inline px-1.5 py-0.5 text-[10px] bg-slate-100 rounded border border-slate-200">⌘K</kbd>
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-sm flex items-start justify-center pt-24 px-4"
          onClick={() => setOpen(false)} data-testid="global-search-modal">
          <div onClick={(e) => e.stopPropagation()}
            className="rounded-2xl w-full max-w-2xl overflow-hidden"
            style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-5)' }}>
            <div className="flex items-center px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
              {isCommand ? (
                <Sparkles size={18} style={{ color: 'var(--accent-copper)' }} className="mr-3" />
              ) : (
                <Search size={18} style={{ color: 'var(--text-muted)' }} className="mr-3" />
              )}
              <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={isCommand ? 'AI parancs — pl. új számla Kovács Kft 500000 Ft 14 nap…' : 'Keress ügyfelet, számlát, terméket, szállítót… vagy kezdd /-jellel AI parancshoz'}
                data-testid="global-search-input"
                className="flex-1 outline-none text-base bg-transparent"
                style={{ color: 'var(--text-primary)' }}
              />
              {(loading || aiParsing) && <Loader2 size={16} className="animate-spin mr-2" style={{ color: 'var(--text-muted)' }} />}
              {voiceSupported && (
                <button
                  onClick={startListening}
                  data-testid="voice-search-btn"
                  className="w-8 h-8 rounded-md flex items-center justify-center transition-all mr-1 relative"
                  style={{
                    backgroundColor: listening ? 'var(--error)' : 'transparent',
                    color: listening ? '#FFFFFF' : 'var(--text-muted)',
                  }}
                  onMouseEnter={(e) => { if (!listening) e.currentTarget.style.backgroundColor = 'var(--background-base)'; }}
                  onMouseLeave={(e) => { if (!listening) e.currentTarget.style.backgroundColor = 'transparent'; }}
                  aria-label={listening ? 'Hangfelismerés leállítása' : 'Hangparancs indítása'}
                  title={listening ? 'Hangfelismerés leállítása (figyelek…)' : 'Hangparancs (magyar)'}
                >
                  {listening ? (
                    <>
                      <Mic size={15} />
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full animate-ping" style={{ backgroundColor: '#FFFFFF' }} />
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full" style={{ backgroundColor: '#FFFFFF' }} />
                    </>
                  ) : (
                    <MicOff size={15} strokeWidth={1.75} />
                  )}
                </button>
              )}
              <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center" style={{ color: 'var(--text-muted)' }}>
                <X size={16} />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {/* Voice listening banner */}
              {listening && (
                <div
                  data-testid="voice-listening-banner"
                  className="px-5 py-4 border-b flex items-center gap-3 animate-fade-in"
                  style={{ borderColor: 'var(--border)', backgroundColor: 'var(--error-soft)' }}
                >
                  <div className="relative">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--error)', color: '#FFF' }}>
                      <Mic size={16} />
                    </div>
                    <div className="absolute inset-0 rounded-full animate-ping" style={{ backgroundColor: 'var(--error)', opacity: 0.4 }} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold" style={{ color: 'var(--error)' }}>Figyelek… beszélj magyarul</p>
                    <p className="text-[11px]" style={{ color: 'var(--text-secondary)' }}>pl. „új számla Kovács Kft részére 500 ezer forint 14 nap"</p>
                  </div>
                  <button
                    onClick={stopListening}
                    className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                    style={{ backgroundColor: 'var(--error)', color: '#FFF' }}
                  >
                    Leállítás
                  </button>
                </div>
              )}
              {/* Command mode */}
              {isCommand && (
                <>
                  {/* Pending parse hint */}
                  {q.trim().length < 4 && (
                    <div className="px-5 py-4">
                      <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>
                        <Sparkles size={11} className="inline mr-1" style={{ color: 'var(--accent-copper)' }} />
                        AI parancs — írj magyarul
                      </p>
                      <div className="space-y-1.5">
                        {EXAMPLE_COMMANDS.map(cmd => (
                          <button
                            key={cmd}
                            data-testid={`ai-example-${cmd.slice(0, 10)}`}
                            onClick={() => setQ(cmd)}
                            className="block w-full text-left px-3 py-2 rounded-lg text-xs transition-colors hover:bg-slate-50"
                            style={{ color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace' }}
                          >
                            {cmd}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiParsing && q.trim().length >= 4 && (
                    <div className="px-5 py-6 flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      <Loader2 size={14} className="animate-spin" />
                      <span>Gemini értelmezi a parancsot…</span>
                    </div>
                  )}

                  {aiResult && !aiParsing && (
                    <div className="p-5" data-testid="ai-command-result">
                      {aiResult.action === 'unknown' ? (
                        <div className="p-4 rounded-xl border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--background-base)' }}>
                          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>Nem értelmezhető</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{aiResult.suggestion || 'Próbáld másképp megfogalmazni'}</p>
                        </div>
                      ) : (
                        <button
                          onClick={executeAiCommand}
                          data-testid="ai-command-execute"
                          className="w-full group relative overflow-hidden rounded-xl p-4 text-left transition-transform active:scale-[0.99]"
                          style={{ background: 'linear-gradient(135deg, var(--accent-copper), var(--accent-copper-hover))', color: 'var(--accent-copper-fg)' }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
                              <Sparkles size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[10px] font-semibold uppercase tracking-wider opacity-80 mb-0.5">AI javaslat</p>
                              <p className="text-sm font-semibold leading-snug">{aiResult.suggestion}</p>
                              {aiResult.params && Object.keys(aiResult.params).length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {Object.entries(aiResult.params).slice(0, 6).map(([k, v]) => (
                                    <span key={k} className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                                      {k}: {String(v)}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-md" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                              <CornerDownLeft size={12} /> Enter
                            </div>
                          </div>
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* Normal search mode */}
              {!isCommand && (
                <>
                  {results.length === 0 && q && !loading && (
                    <div className="px-6 py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Nincs találat &quot;{q}&quot; kifejezésre</div>
                  )}
                  {results.length === 0 && !q && (
                    <div className="px-6 py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                      Kezdj el gépelni — átnézi az ügyfeleket, számlákat (kimenő + bejövő), szerződéseket, ajánlatokat, szolgáltatásokat és a dokumentum-archívumot.<br/>
                      <span className="text-xs mt-1 inline-block">Vagy írj <kbd className="font-semibold" style={{ color: 'var(--accent-copper)' }}>/</kbd> jellel AI parancsot.</span>
                    </div>
                  )}
                  {results.map((r, i) => {
                    const meta = TYPE_META[r.type] || TYPE_META.invoice;
                    const Icon = meta.icon;
                    return (
                      <button key={`${r.type}-${r.id}`} onClick={() => goResult(r)}
                        data-testid={`search-result-${i}`}
                        onMouseEnter={() => setSelected(i)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${selected === i ? 'bg-slate-50' : 'hover:bg-slate-50'}`}>
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${meta.bg} flex-shrink-0`}>
                          <Icon size={16} className={meta.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate" style={{ color: 'var(--text-primary)' }}>{r.label}</p>
                          {r.subtitle && <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{r.subtitle}</p>}
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${meta.bg} ${meta.color} font-medium`}>{meta.label}</span>
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            <div className="px-4 py-2 border-t text-[11px] flex items-center gap-4" style={{ backgroundColor: 'var(--background-base)', borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
              {isCommand ? (
                <>
                  <span><Sparkles size={11} className="inline mr-0.5" style={{ color: 'var(--accent-copper)' }} /> AI parancs mód</span>
                  <span><kbd className="px-1 py-0.5 rounded border" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border)' }}>↵</kbd> végrehajtás</span>
                </>
              ) : (
                <>
                  <span><kbd className="px-1 py-0.5 rounded border" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border)' }}>↑↓</kbd> navigálás</span>
                  <span><kbd className="px-1 py-0.5 rounded border" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border)' }}>↵</kbd> megnyitás</span>
                  <span><kbd className="px-1 py-0.5 rounded border" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border)' }}>/</kbd> AI parancs</span>
                </>
              )}
              <span className="ml-auto"><kbd className="px-1 py-0.5 rounded border" style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border)' }}>Esc</kbd> bezárás</span>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
